import { Spacing } from '@billy/design-system'
import { Fragment, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { FecImportConfirmationKind } from '../../../../enums/fecImportConfirmationKind'
import type { FecImportItem } from '../../../../query-api'
import { getFecImportAnalysisErrorTranslation } from '../../../../utils/getFecImportAnalysisErrorTranslation'
import { getFecImportConfirmationErrorCopy } from '../../../../utils/getFecImportConfirmationErrorCopy'
import { isFecImportAnalysisComplete } from '../../../../utils/isFecImportAnalysisComplete'
import { isFecImportAnalysisFailed } from '../../../../utils/isFecImportAnalysisFailed'
import { FecImportOutputList } from '../FecImportOutputList'

interface FecImportConfirmationContentProps {
  confirmationKind: FecImportConfirmationKind
  fecImports: FecImportItem[]
  sessionFiles: File[]
}

const groupFailedImportsByTitle = (fecImports: FecImportItem[]): Map<string, FecImportItem[]> => {
  const groups = new Map<string, FecImportItem[]>()

  for (const fecImport of fecImports) {
    const { titleKey } = getFecImportAnalysisErrorTranslation(fecImport)
    const group = groups.get(titleKey) ?? []

    group.push(fecImport)
    groups.set(titleKey, group)
  }

  return groups
}

export const FecImportConfirmationContent = ({
  confirmationKind,
  fecImports,
  sessionFiles,
}: FecImportConfirmationContentProps): ReactElement => {
  const { t } = useTranslation()
  const completedFecImports = fecImports.filter(isFecImportAnalysisComplete)
  const failedFecImports = fecImports.filter(isFecImportAnalysisFailed)
  const failedImportGroups = groupFailedImportsByTitle(failedFecImports)

  if (confirmationKind === 'success') {
    return (
      <FecImportOutputList
        fecImports={completedFecImports}
        sessionFiles={sessionFiles}
        title={t('settings.organization.accounting.fec_file_import.modal.confirmation_step.title')}
      />
    )
  }

  if (confirmationKind === 'partial') {
    return (
      <Fragment>
        <FecImportOutputList
          fecImports={completedFecImports}
          gap={Spacing.S8}
          sessionFiles={sessionFiles}
          title={t('settings.organization.accounting.fec_file_import.modal.confirmation_step.files_analyzed_title')}
        />
        {[...failedImportGroups].map(([titleKey, imports]) => (
          <FecImportOutputList fecImports={imports} key={titleKey} sessionFiles={sessionFiles} title={t(titleKey)} />
        ))}
      </Fragment>
    )
  }

  const { messageKey, titleKey } = getFecImportConfirmationErrorCopy({
    confirmationKind,
    failedFecImports,
    fecImports,
  })

  return (
    <FecImportOutputList
      fecImports={fecImports}
      overrideErrorMessageKey={messageKey}
      sessionFiles={sessionFiles}
      title={t(titleKey)}
    />
  )
}
